import { useMutation } from "@apollo/client";
import moment from "moment-timezone";

import {
  PUBLISH_TICKET_SCHEDULE_DRAFT,
  UPDATE_TICKET_SCHEDULE_DRAFT,
} from "~/api";
import { Ticket } from "~/api/_fragments";
import { OPERATIONS } from "~/constants/scheduling";

export const useUpdateTicketScheduleDraft = () => {
  const [updateTicketScheduleDraft, { loading, error }] = useMutation(
    UPDATE_TICKET_SCHEDULE_DRAFT,
  );

  return {
    loading,
    error,
    updateScheduleDraft: async (input) =>
      updateTicketScheduleDraft({
        variables: { input },
      }),
  };
};

export const usePublishTicketScheduleDraft = () => {
  const [publishTicketScheduleDraft, { loading, error }] = useMutation(
    PUBLISH_TICKET_SCHEDULE_DRAFT,
  );

  return {
    loading,
    error,
    publishScheduleDraft: async (input) =>
      publishTicketScheduleDraft({
        variables: {
          input: {
            assignees: input.assignees,
            dateRange: input.dateRange,
            version: input.version,
          },
        },
        update(cache) {
          const ticketMap = new Map();

          input.operations
            .filter(
              (operation) => operation.operationType === OPERATIONS.TICKET,
            )
            .forEach((operation) => {
              const { ticket } = operation;

              const matchesDateRange =
                moment(ticket.schedule.startTime).isSameOrAfter(
                  moment(input.dateRange.startDate),
                ) &&
                moment(ticket.schedule.endTime).isSameOrBefore(
                  moment(input.dateRange.endDate),
                );

              let matchesAssignees = true;
              if (input.assignees.length > 0)
                matchesAssignees = input.assignees.some((id) =>
                  ticket.assignees.includes(id),
                );

              if (matchesDateRange && matchesAssignees)
                ticketMap.set(ticket.id, ticket);
            });

          ticketMap.forEach((value, key) => {
            const cacheId = cache.identify({ __typename: "Ticket", id: key });
            const ticketRef = cache.readFragment({
              id: cacheId,
              fragment: Ticket.fragments.TicketListData,
              fragmentName: "TicketListData",
            });

            if (!ticketRef) return;

            const updatedData = {
              status: value.status,
              assignees: (value.assignees || []).map(
                (assigneeId) => input.agentsMap[assigneeId],
              ),
              resources: (value.resources || []).map((resourceId) => ({
                _id: resourceId,
                __typeName: "Resource",
              })),
              schedule: {
                ...value.schedule,
                __typename: "Schedule",
              },
              dueDate: value.dueDate,
            };

            // @ts-ignore
            const updatedRef = { ...ticketRef, ...updatedData };

            cache.writeFragment({
              data: updatedRef,
              fragment: Ticket.fragments.TicketListData,
              fragmentName: "TicketListData",
            });
          });

          cache.gc();
        },
      }),
  };
};
